import React, { useState, useEffect, useCallback } from 'react';
import { useStepWorkspaceRevision } from '../context/StepWorkspaceContext';
import Header, { BreadcrumbItem } from '../components/Header';
import ContentReader from '../components/ContentReader';
import VideoModal from '../components/VideoModal';
import HistoryButton from '../components/HistoryButton';
import StepHelpButton from '../components/StepHelpButton';
import HistoryModal from '../components/HistoryModal';
import Step9ListModal from '../components/Step9ListModal';
import { progressService, ProgressItem, JourneyProgress } from '../services/progressService';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';
import StepShareDialog from '../components/StepShareDialog';
import { useStepShareDialog } from '../hooks/useStepShareDialog';
import { useRelocalizeStepActions } from '../hooks/useRelocalizeStepActions';
import { withShareButton } from '../utils/stepShareUtils';
import { loadAmendsPersons } from '../utils/stepShareDataLoaders';
import { mergeStepActions, stepDataWithActions, buildStepDataWithActions } from '../utils/mergeStepActions';
import { perfMark, perfSummary, perfTimeAsync } from '../utils/perfTimer';
import { getStepActionSchema } from '../config/stepActionSchemas';
import { STEP_UI } from '../config/uiStrings';
import { getStepActionLabel } from '../config/stepActionLabels';
import { getStepActionDescription } from '../config/stepActionDescriptions';
import {
  getActionsIntro,
  getActionsToCompleteTitle,
  getCongratsBody,
  getCongratsHeading,
  getGuidanceModalTitle,
  getNextStepNavLabel,
  getPrevStepNavLabel,
  getRefreshPageLabel,
  getStepBreadcrumbs,
  getStepLoadingMessage,
  getStepPageQuote,
  getStepPageTitle,
  getVideoGuideTitle,
} from '../config/stepPageCopy';
import { t } from '../i18n/translate';

interface Step9Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const Step9: React.FC<Step9Props> = ({ user, onLogin, onLogout }) => {
  const stepWorkspaceRevision = useStepWorkspaceRevision();
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isListModalOpen, setIsListModalOpen] = useState(false);
  const [listCount, setListCount] = useState(0);
  const [plannedCount, setPlannedCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [amendsPersons, setAmendsPersons] = useState<any[]>([]);
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: getStepActionLabel(9, 1),
      description: getStepActionDescription(9, 1),
      completed: false,
      hasButton: true,
      buttonText: STEP_UI.READ_GREEN_BOOK,
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: getStepActionLabel(9, 2),
      description: getStepActionDescription(9, 2),
      completed: false,
      buttons: [
        {
          text: STEP_UI.READ_GUIDANCE,
          action: () => setIsGuidanceOpen(true),
          variant: 'primary'
        },
        {
          text: STEP_UI.WATCH_VIDEO,
          action: () => setIsVideoModalOpen(true),
          variant: 'secondary'
        }
      ]
    },
    {
      id: 3,
      title: getStepActionLabel(9, 3),
      description: getStepActionDescription(9, 3),
      completed: false,
      hasButton: true,
      buttonText: STEP_UI.OPEN_AMENDS_LIST,
      buttonAction: () => setIsListModalOpen(true)
    },
    {
      id: 4,
      title: getStepActionLabel(9, 4),
      description: getStepActionDescription(9, 4),
      completed: false
    },
    {
      id: 5,
      title: getStepActionLabel(9, 5),
      description: getStepActionDescription(9, 5),
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  useRelocalizeStepActions(originalActions, setActions);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();

  const getShareContext = useCallback(() => ({ amendsPersons }), [amendsPersons]);
  const { openShareDialog, shareDialogProps } = useStepShareDialog(9, user, actions, getShareContext);

  const breadcrumbs: BreadcrumbItem[] = getStepBreadcrumbs(9);

  // Check if a person entry has amends completed (checkbox checked)
  const isPersonCompleted = (person: any): boolean => {
    if (!person || typeof person !== 'object') return false;
    return person.amendsCompleted === true;
  };

  // Check if a person entry has an amends plan
  const hasAmendsPlan = (person: any): boolean => {
    if (!person || typeof person !== 'object') return false;
    const plan = (person.amendsPlan && typeof person.amendsPlan === 'string') ? person.amendsPlan.trim() : '';
    return plan.length > 0;
  };

  // Load list count, planned count, and completion status
  const loadListCount = async () => {
    if (!user) {
      setListCount(0);
      setPlannedCount(0);
      setCompletedCount(0);
      setAmendsPersons([]);
      return;
    }
    
    try {
      const personItems = await loadAmendsPersons('step9-persons');
      setAmendsPersons(personItems);
      setListCount(personItems.length);
      
      // Count planned entries (have amendsPlan content)
      const planned = personItems.filter(item => hasAmendsPlan(item)).length;
      setPlannedCount(planned);
      
      // Count completed entries (amendsCompleted checkbox checked)
      const completed = personItems.filter(item => isPersonCompleted(item)).length;
      setCompletedCount(completed);
    } catch (error) {
      // If no list exists yet, counts are 0
      setListCount(0);
      setPlannedCount(0);
      setCompletedCount(0);
      setAmendsPersons([]);
    }
  };

  const applyLoadedStepProgress = async (
    existingProgress: ProgressItem | null,
    journeyProgress: JourneyProgress | null
  ) => {
    if (journeyProgress) {
      setIsOptedOut(journeyProgress.opted_out);
    }

    if (existingProgress) {
      const savedData = existingProgress.data || {};
      setStepData(existingProgress);

      if (savedData.actions) {
        const merged = mergeStepActions(9, originalActions, savedData, !!existingProgress.completed);
        setActions(merged.actions);
        if (merged.migrationApplied) {
          await progressService.updateStepProgress({
            program: 'SAA',
            track: '12-steps',
            step_number: 9,
            step_title: 'Step Nine',
            completed: !!existingProgress.completed,
            data: buildStepDataWithActions(merged.actions, merged.actionSchemaVersion, savedData),
          });
        }
      }
    }
  };

  const refreshStepData = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }

    setIsInitializing(true);

    try {
      perfMark('Step9', 'refreshStepData start');
      const [{ steps, journeyProgress }] = await Promise.all([
        perfTimeAsync('Step9 loadStepPagesProgress (parallel)', () =>
          progressService.loadStepPagesProgress('SAA', '12-steps', [9], { includeJourney: true })
        ),
        loadListCount(),
      ]);

      await applyLoadedStepProgress(steps[9] ?? null, journeyProgress);
      clearError();
      perfSummary('Step Nine page');
    } catch (err: any) {
      console.error('Error loading step progress:', err);
      handleError(err, t('step.page.error.load'));
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    refreshStepData();
  }, [user, stepWorkspaceRevision]);

  const handleHistoryStepUpdated = () => {
    setIsHistoryModalOpen(false);
    if (user) {
      void refreshStepData();
    }
  };

  const getCurrentActionIndex = () => {
    return actions.findIndex(action => !action.completed);
  };

  const isActionAvailable = (actionIndex: number) => {
    if (actionIndex === 0) return true;
    return actions.slice(0, actionIndex).every(a => a.completed);
  };

  const canCompleteAction = (actionId: number) => {
    if (actionId === 4 || actionId === 5) {
      const action3 = actions.find(a => a.id === 3);
      return action3 ? action3.completed : false;
    }
    return true;
  };

  const handleActionChange = async (updatedActions: ActionItem[]) => {
    // For other actions, save to backend
    if (!user) {
      handleError(new Error('User not logged in'), t('step.page.error.sign_in'));
      return;
    }
    
    try {
      // Update local state
      setActions(updatedActions);
      
      // Save to backend
      const allCompleted = updatedActions.every(action => action.completed);
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 9,
        step_title: 'Step Nine',
        completed: allCompleted,
        data: stepDataWithActions(updatedActions, getStepActionSchema(9).actionSchemaVersion)
      });
      
      // Update step data for history modal
      if (updatedStepData && updatedStepData.step) {
        setStepData(updatedStepData.step);
      }
      
      // Update status note
      const completedCount = updatedActions.filter(a => a.completed).length;
      await progressService.updateStatus({
        program: 'SAA',
        track: '12-steps',
        status_note: `Working Step Nine - ${completedCount} of 5 actions completed`,
        progress_milestone: allCompleted ? 'step9_completed' : 'step9_in_progress'
      });
    } catch (err: any) {
      console.error('Error updating action progress:', err);
      handleError(err, t('step.page.error.save_progress'));
    }
  };

  // Check if all actions are completed
  const allCompleted = actions.every(action => action.completed);

  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>{getStepPageTitle(9)}</h1>
            <StepHelpButton stepKey="app" />
            {stepData && (
              <HistoryButton
                stepNumber={9}
                stepTitle={getStepPageTitle(9)}
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>{getStepPageQuote(9)}
          </p>
        </div>

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>{getRefreshPageLabel()}</button>
              </>
            )}
          </div>
        )}

        {!user && !isInitializing && !errorMessage && (
          <StepLoginPrompt onLogin={onLogin} />
        )}

        {user && isInitializing && !errorMessage && (
          <div className={styles.loadingMessage}>
            <p>{getStepLoadingMessage()}</p>
          </div>
        )}

        {user && !isInitializing && !errorMessage && (
          <div className={styles.actionsContainer}>
            <h2 className={styles.actionsTitle}>{getActionsToCompleteTitle(9)}</h2>
            <p className={styles.actionsDescription}>{getActionsIntro()}</p>
            
            <StepActions
              actions={withShareButton(9, actions, openShareDialog)}
              onActionChange={handleActionChange}
              canCompleteAction={canCompleteAction}
              stepNumber={9}
              renderActionContent={(action, index) => {
                if (action.id === 3) {
                  return (
                    <div style={{ 
                      marginTop: '0.5rem', 
                      color: '#6b7280', 
                      fontSize: '0.875rem',
                      paddingLeft: '56px' // Align with button: actionNumber (32px) + margin-right (0.75rem ≈ 12px) + gap (0.75rem ≈ 12px) = 56px
                    }}>
                      {listCount === 0 ? (
                        <p style={{ margin: 0, fontStyle: 'italic' }}>
                          {t('step.9.helper.copy_from_step8')}
                        </p>
                      ) : (
                        <span>
                          {t('step.9.helper.count', {
                            count: listCount,
                            personWord:
                              listCount === 1
                                ? t('step.8.helper.person')
                                : t('step.8.helper.persons'),
                            planned: plannedCount,
                            completed: completedCount,
                          })}
                        </span>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />

            {allCompleted && (
              <>
                <div className={styles.completionMessage}>
                  <h3>{getCongratsHeading()}</h3>
                  <p>{getCongratsBody(9)}</p>
                </div>
                <div className={styles.stepNavigation}>
                  <div className={styles.navigationLinks}>
                    <a href="/saa/12-steps/step-8" className={styles.prevStepLink}>
                      {getPrevStepNavLabel(9)}
                    </a>
                    <span></span>
                    <a href="/saa/12-steps/step-10" className={styles.nextStepLink}>
                      {getNextStepNavLabel(9)}
                    </a>
                  </div>
                </div>
              </>
            )}

            {!allCompleted && (
              <div className={styles.stepNavigation}>
                <div className={styles.navigationLinks}>
                  <a href="/saa/12-steps/step-8" className={styles.prevStepLink}>
                    {getPrevStepNavLabel(9)}
                  </a>
                  <span></span>
                  <a href="/saa/12-steps/step-10" className={styles.nextStepLink}>
                    {getNextStepNavLabel(9)}
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
      {/* Video Modal for Step Nine */}
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="uEkrAkoJjwU"
        title={getVideoGuideTitle(9)}
      />

      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-nine start>>"
        endTag="<<step-nine end>>"
      />

      {/* ContentReader for How to Work Step Nine */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_9_Guidance.md"
        title={getGuidanceModalTitle(9)}
        startTag="<<working-step-nine start>>"
        endTag="<<working-step-nine end>>"
      />

      {/* Step 9 List Modal */}
      <Step9ListModal
        isOpen={isListModalOpen}
        onClose={() => setIsListModalOpen(false)}
        user={user}
        onListUpdate={loadListCount}
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={9}
        stepTitle={getStepPageTitle(9)}
        stepData={stepData}
        onStepUpdated={handleHistoryStepUpdated}
      />

      <StepShareDialog {...shareDialogProps} />
    </div>
  );
};

export default Step9; 
